import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Eventos() {
  const [eventos, setEventos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('Todos')
  const [heroConfig, setHeroConfig] = useState(null)
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    fetchEventos()
    loadHeroConfig()
    loadSEOConfig()
    loadFAQs()
  }, [])

  const fetchEventos = async () => {
    try {
      setLoading(true)
      const { data, error } = await db.getEventos()
      if (error) throw error
      setEventos(data || [])
    } catch (error) {
      console.error('Error fetching eventos:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadHeroConfig = async () => {
    const { data } = await db.getConfiguracionPortada('eventos')
    setHeroConfig(data || null)
  }

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('eventos')
    setSeoConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'eventos')
    setFaqs(data || [])
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('es-PE', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const filters = ['Todos', 'Activo', 'Próximo', 'Finalizado']

  const filteredEventos = eventos.filter(evento => {
    if (filter === 'Todos') return true
    return evento.estado === filter
  })

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Eventos - Plaza Aurora'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Descubre los próximos eventos en Plaza Aurora Outlet. Promociones especiales, actividades y más.'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Eventos - Plaza Aurora'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre los próximos eventos en Plaza Aurora Outlet. Promociones especiales, actividades y más.'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Eventos - Plaza Aurora'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre los próximos eventos en Plaza Aurora Outlet. Promociones especiales, actividades y más.'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
        
        {/* LLM Discovery - JSON Feeds */}
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/events.json`} title="Events List" />
      </Helmet>
      
      {/* Breadcrumb JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Eventos",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos`
            }
          ]
        })}
      </script>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}

      <main>
        <div className="container" style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          {/* Hero Section */}
          <section id="hero_main" role="region" aria-label="Hero Eventos" style={{ marginTop: '1.5rem' }}>
            <div className="heroContainer" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 0.9fr',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.25rem',
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden'
            }}>
              <div className="heroLeft" style={{ padding: '1rem' }}>
                <h1 className="heroTitle" style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--font-weight-bold)',
                  lineHeight: '0.95',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>
                  {heroConfig?.hero_titulo || 'EVENTOS PARA TODA LA FAMILIA EN LIMA NORTE'}<br/>
                </h1>
                <p className="heroDescription" style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lead)',
                  color: 'var(--color-text-muted)',
                  maxWidth: '48ch',
                  lineHeight: 'var(--line-height-relaxed)',
                  marginBottom: '1.5rem'
                }}>
                  {heroConfig?.hero_descripcion || 'Actividades, promociones y eventos especiales que tenemos para ti 🎉'}
                </p>
                <div className="heroActions" style={{ display: 'flex', gap: '0.75rem' }}>
                  <Link 
                    to={heroConfig?.hero_boton_1_url || '/eventos'} 
                    className={heroConfig?.hero_boton_1_estilo === 'outline' ? 'btnOutline' : 'btnPrimary'} 
                    style={{
                      padding: '0.6rem 1.1rem',
                      background: 'var(--color-cta-bg)',
                      color: 'var(--color-cta-text)',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                      fontSize: 'var(--font-size-body)',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.background = 'var(--color-cta-bg)'
                      e.currentTarget.style.color = 'var(--color-cta-text)'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-600)'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.background = 'var(--color-cta-bg)'
                        e.currentTarget.style.color = 'var(--color-cta-text)'
                      }, 150)
                    }}
                  >
                    {heroConfig?.hero_boton_1_texto || 'Ver Eventos'}
                  </Link>
                  <Link to={heroConfig?.hero_boton_2_url || '/ubicanos'} className={heroConfig?.hero_boton_2_estilo === 'primary' ? 'btnPrimary' : 'btnOutline'} style={{
                    padding: '0.6rem 1.1rem',
                    background: 'transparent',
                    border: '1px solid var(--color-stroke)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-primary)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}>
                    {heroConfig?.hero_boton_2_texto || 'Ubícanos'}
                  </Link>
                </div>
              </div>
              <div className="heroRight" style={{ height: '100%' }}>
                <img className="heroImage" src={heroConfig?.hero_imagen_url || 'https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/home/tarjetas/1761616076699_fb9831bcb1d29ee2edd8a0eb29b291fe.jpg'} alt={heroConfig?.hero_imagen_alt || 'Próximos Eventos'} style={{
                  width: '100%',
                  height: '420px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-md)'
                }}/>
              </div>
            </div>
          </section>
        </div>

        {/* Promo Strip - Full Width */}
        <section id="promo_strip" role="complementary" aria-label="Promo marquee" style={{ 
          marginTop: '1.5rem', 
          overflow: 'hidden', 
          display: heroConfig?.marquee_activo === false ? 'none' : 'block'
        }}>
          {heroConfig?.marquee_link_url
            ? (heroConfig.marquee_link_url.startsWith('/')
                ? (
                  <Link to={heroConfig.marquee_link_url} style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS  •  PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS'}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <a href={heroConfig.marquee_link_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS  •  PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS'}
                      </div>
                    </div>
                  </a>
                )
              )
            : (
              <div className="marquee" style={{
                background: 'var(--color-black-block)',
                color: 'var(--color-cta-text)',
                fontFamily: 'var(--font-display)',
                fontSize: '0.875rem',
                padding: '0.5rem 0',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}>
                <div className="marquee-content" style={{
                  display: 'inline-block',
                  animation: 'scroll-left 25s linear infinite',
                  paddingLeft: '100%'
                }}>
                  {heroConfig?.marquee_texto || '★ PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS  •  PROMOCIONES ESPECIALES  •  ACTIVIDADES EXCLUSIVAS  •  EVENTOS IMPERDIBLES  •  SORTEOS Y PREMIOS'}
                </div>
              </div>
            )
          }
        </section>

        <div className="container" style={{ maxWidth: 'var(--container-max-width)', margin: '0 auto', padding: '0 2rem' }}>
          <section id="events_main" role="region" aria-label="Upcoming Events" style={{ marginTop: '1.5rem' }}>
            {/* Header */}
            <div style={{ marginBottom: '2.5rem' }}>
              <h2 style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0
              }}>Eventos</h2>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div className="filters" style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                {filters.map((f) => (
                  <button
                    key={f}
                    className={`filter ${filter === f ? 'active' : ''}`}
                    onClick={() => setFilter(f)}
                    style={{
                      padding: '0.4rem 0.7rem',
                      borderRadius: 'var(--radius-sm)',
                      background: filter === f ? 'var(--brand-orange)' : 'var(--color-tag-bg)',
                      border: `1px solid ${filter === f ? 'var(--brand-orange)' : 'var(--color-stroke)'}`,
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-small)',
                      transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                      color: filter === f ? 'white' : 'var(--color-text-primary)',
                      fontWeight: filter === f ? 'var(--font-weight-bold)' : 'var(--font-weight-regular)'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== f) {
                        e.currentTarget.style.backgroundColor = 'var(--brand-orange-50)'
                        e.currentTarget.style.borderColor = 'var(--brand-orange-300)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== f) {
                        e.currentTarget.style.backgroundColor = 'var(--color-tag-bg)'
                        e.currentTarget.style.borderColor = 'var(--color-stroke)'
                      }
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-orange-200)'
                    }}
                    onTouchStart={(e) => {
                      if (filter !== f) {
                        e.currentTarget.style.backgroundColor = 'var(--brand-orange-50)'
                        e.currentTarget.style.borderColor = 'var(--brand-orange-300)'
                      }
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-orange-200)'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.boxShadow = 'none'
                        if (filter !== f) {
                          e.currentTarget.style.backgroundColor = 'var(--color-tag-bg)'
                          e.currentTarget.style.borderColor = 'var(--color-stroke)'
                        }
                      }, 150)
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Events Grid */}
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem'
              }}>
                <LoadingSpinner message="" size={64} />
              </div>
            ) : filteredEventos.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'var(--color-accent-gray)',
                borderRadius: 'var(--radius-md)'
              }}>
                <p style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-body)',
                  color: 'var(--color-text-muted)'
                }}>No se encontraron eventos</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem',
                marginBottom: '3rem'
              }}>
                {filteredEventos.map((evento) => (
                  <Link
                    key={evento.id}
                    to={`/eventos/${evento.id}`}
                    style={{
                      background: 'white',
                      borderRadius: 'var(--radius-lg)',
                      overflow: 'hidden',
                      boxShadow: 'none',
                      border: 'none',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-card)'
                      e.currentTarget.style.outline = '1px solid var(--brand-orange-200)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.outline = 'none'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = 'scale(0.98)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-orange-200)'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                      }, 150)
                    }}
                  >
                    {/* Event Image */}
                    <div style={{
                      height: '200px',
                      background: 'var(--color-accent-gray)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {evento.imagen_url ? (
                        <img
                          src={evento.imagen_url}
                          alt={evento.nombre}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '3rem' }}>🎉</span>
                      )}
                      {/* Estado Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem',
                        background: evento.estado === 'Activo' ? '#0ACC66' : evento.estado === 'Próximo' ? '#F79431' : '#7E7E82',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-micro)',
                        fontWeight: 'var(--font-weight-bold)'
                      }}>
                        {evento.estado}
                      </div>
                    </div>

                    {/* Event Info */}
                    <div style={{ padding: '1rem' }}>
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        color: 'var(--color-text-muted)',
                        margin: '0 0 0.5rem 0'
                      }}>
                        {formatDate(evento.fecha_inicio)}
                        {evento.horario && ` • ${evento.horario}`}
                      </p>
                      <h3 style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-text-primary)',
                        margin: '0 0 0.5rem 0'
                      }}>
                        {evento.nombre}
                      </h3>
                      {evento.descripcion && (
                        <p style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-small)',
                          color: 'var(--color-text-muted)',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {evento.descripcion}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* FAQs Section */}
          {faqs.length > 0 && (
            <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-display-l)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                textAlign: 'center',
                marginBottom: '2rem'
              }}>PREGUNTAS FRECUENTES</h2>
              
              <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {faqs.map((faq) => (
                  <details key={faq.id} style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.5rem',
                    border: '1px solid var(--color-stroke)',
                    cursor: 'pointer'
                  }}>
                    <summary style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-h4)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      listStyle: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {faq.pregunta}
                      <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                    </summary>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      lineHeight: '1.6',
                      marginTop: '1rem',
                      whiteSpace: 'pre-line'
                    }}>
                      {faq.respuesta}
                    </p>
                  </details>
                ))}
              </div>
              
              {/* FAQPage JSON-LD */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.pregunta,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": faq.respuesta
                    }
                  }))
                })}
              </script>
            </section>
          )}
        </div>
      </main>

      <style>{`
        @media (min-width: 600px) and (max-width: 1023px) {
          .container {
            padding: 0 1.5rem !important;
          }
          .container > section > div:last-child {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 599px) {
          .container {
            padding: 0 1rem !important;
            padding-top: 0rem !important;
          }
          .container > section > div:last-child {
            grid-template-columns: 1fr !important;
          }
        }

        .filter:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </>
  )
}


